import _Math$log from 'babel-runtime/core-js/math/log2';
/**
 * @license
 * Copyright 2020 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @file Defines lerp/invlerp functionality for all supported data types.
 */
import { DataType } from '../util/data_type';
import { Uint64 } from '../util/uint64';
import { dataTypeShaderDefinition, getShaderType, glsl_addSaturateInt32, glsl_addSaturateUint32, glsl_addSaturateUint64, glsl_compareLessThanUint64, glsl_equalUint64, glsl_shiftLeftSaturateUint32, glsl_shiftLeftUint64, glsl_shiftRightUint64, glsl_subtractSaturateInt32, glsl_subtractSaturateUint32, glsl_subtractSaturateUint64, glsl_subtractUint64, glsl_uint64 } from './shader_lib';
export const dataTypeShaderLerpParametersType = {
    [DataType.UINT8]: 'vec2',
    [DataType.INT8]: 'vec2',
    [DataType.UINT16]: 'vec2',
    [DataType.INT16]: 'vec2',
    [DataType.FLOAT32]: 'vec2',
    [DataType.UINT32]: 'Uint32LerpParameters',
    [DataType.INT32]: 'Int32LerpParameters',
    [DataType.UINT64]: 'Uint64LerpParameters'
};
const glsl_dataTypeLerpParameters = {
    [DataType.UINT8]: '',
    [DataType.INT8]: '',
    [DataType.UINT16]: '',
    [DataType.INT16]: '',
    [DataType.FLOAT32]: '',
    [DataType.UINT32]: `
struct Uint32LerpParameters {
  uint offset;
  int shift;
  float multiplier;
};
`,
    [DataType.INT32]: `
struct Int32LerpParameters {
  int offset;
  int shift;
  float multiplier;
};
`,
    [DataType.UINT64]: [glsl_uint64, `
struct Uint64LerpParameters {
  uint64_t offset;
  int shift;
  float multiplier;
};
`]
};
function getFloatInvlerpImpl(dataType) {
    const shaderDataType = getShaderType(dataType);
    let code = `
float computeInvlerp(${shaderDataType} inputValue, vec2 p) {
  float outputValue = float(toRaw(inputValue));
  outputValue = (outputValue - p[0]) * p[1];
  return outputValue;
}
`;
    return [dataTypeShaderDefinition[dataType], code];
}
function getInt32InvlerpImpl(dataType) {
    const shaderDataType = getShaderType(dataType);
    let scalarType = dataType === DataType.INT32 ? 'int' : 'uint';
    let pType = dataTypeShaderLerpParametersType[dataType];
    return [dataTypeShaderDefinition[dataType], glsl_dataTypeLerpParameters[dataType], `
float computeInvlerp(${shaderDataType} inputValue, ${pType} p) {
  ${scalarType} v = toRaw(inputValue);
  uint x;
  if (v >= p.offset) {
    x = uint(v - p.offset);
  } else {
    x = uint(p.offset - v);
    p.multiplier = -p.multiplier;
  }
  x >>= p.shift;
  return float(x) * p.multiplier;
}
`];
}
export const glsl_dataTypeComputeInvlerp = {
    [DataType.UINT8]: getFloatInvlerpImpl(DataType.UINT8),
    [DataType.INT8]: getFloatInvlerpImpl(DataType.INT8),
    [DataType.UINT16]: getFloatInvlerpImpl(DataType.UINT16),
    [DataType.INT16]: getFloatInvlerpImpl(DataType.INT16),
    [DataType.FLOAT32]: getFloatInvlerpImpl(DataType.FLOAT32),
    [DataType.UINT32]: getInt32InvlerpImpl(DataType.UINT32),
    [DataType.INT32]: getInt32InvlerpImpl(DataType.INT32),
    [DataType.UINT64]: [glsl_uint64, glsl_compareLessThanUint64, glsl_subtractUint64, glsl_shiftRightUint64, glsl_dataTypeLerpParameters[DataType.UINT64], `
float computeInvlerp(uint64_t inputValue, Uint64LerpParameters p) {
  if (compareLessThan(inputValue, p.offset)) {
    inputValue = subtract(p.offset, inputValue);
    p.multiplier = -p.multiplier;
  } else {
    inputValue = subtract(inputValue, p.offset);
  }
  uint shifted = shiftRight(inputValue, p.shift).value[0];
  return float(shifted) * p.multiplier;
}
`]
};
function getFloatLerpImpl(dataType) {
    const shaderDataType = getShaderType(dataType);
    let code = `
${shaderDataType} computeLerp(float inputValue, vec2 p) {
  inputValue = inputValue / p[1] + p[0];
`;
    if (dataType === DataType.FLOAT32) {
        code += `return inputValue;\n`;
    } else {
        code += `return ${DataType[dataType].toLowerCase()}FromFloat(round(inputValue));\n`;
    }
    code += `
}
`;
    return [dataTypeShaderDefinition[dataType], code];
}
function getInt32LerpImpl(dataType) {
    const shaderDataType = getShaderType(dataType);
    let pType = dataTypeShaderLerpParametersType[dataType];
    return [dataTypeShaderDefinition[dataType], glsl_dataTypeLerpParameters[dataType], glsl_shiftLeftSaturateUint32, dataType === DataType.UINT32 ? glsl_addSaturateUint32 : glsl_addSaturateInt32, dataType === DataType.UINT32 ? glsl_subtractSaturateUint32 : glsl_subtractSaturateInt32, `
${shaderDataType} computeLerp(float inputValue, ${pType} p) {
  inputValue = inputValue / p.multiplier;
  uint x = uint(clamp(round(abs(inputValue)), 0.0, 4294967295.0));
  uint xShifted = shiftLeftSaturate(x, p.shift);
  if (inputValue >= 0.0) {
    return ${shaderDataType}(addSaturate(p.offset, xShifted));
  } else {
    return ${shaderDataType}(subtractSaturate(p.offset, xShifted));
  }
}
`];
}
export const glsl_dataTypeComputeLerp = {
    [DataType.UINT8]: getFloatLerpImpl(DataType.UINT8),
    [DataType.INT8]: getFloatLerpImpl(DataType.INT8),
    [DataType.UINT16]: getFloatLerpImpl(DataType.UINT16),
    [DataType.INT16]: getFloatLerpImpl(DataType.INT16),
    [DataType.FLOAT32]: getFloatLerpImpl(DataType.FLOAT32),
    [DataType.UINT32]: getInt32LerpImpl(DataType.UINT32),
    [DataType.INT32]: getInt32LerpImpl(DataType.INT32),
    [DataType.UINT64]: [glsl_uint64, glsl_compareLessThanUint64, glsl_equalUint64, glsl_addSaturateUint64, glsl_subtractSaturateUint64, glsl_shiftRightUint64, glsl_shiftLeftUint64, glsl_dataTypeLerpParameters[DataType.UINT64], `
uint64_t computeLerp(float inputValue, Uint64LerpParameters p) {
  inputValue = inputValue / p.multiplier;
  uint64_t x = uint64_t(uvec2(uint(clamp(round(abs(inputValue)), 0.0, 4294967295.0)), 0u));
  uint64_t shifted = shiftLeft(x, p.shift);
  if (!equals(shiftRight(shifted, p.shift), x)) {
    return uint64_t(uvec2(0xffffffffu, 0xffffffffu));
  }
  if (inputValue >= 0.0) {
    return addSaturate(p.offset, shifted);
  } else {
    return subtractSaturate(p.offset, shifted);
  }
}
`]
};
function defineLerpUniforms(builder, name, dataType) {
    const pName = `uLerpParams_${name}`;
    const bName = `uLerpBounds_${name}`;
    const sName = `uLerpScalar_${name}`;
    let code = ``;
    switch (dataType) {
        case DataType.INT8:
        case DataType.UINT8:
        case DataType.INT16:
        case DataType.UINT16:
        // {uint,int}{8,16} can be converted with float32 without any loss of precision
        case DataType.FLOAT32:
            builder.addUniform('vec2', pName);
            break;
        case DataType.INT32:
        case DataType.UINT32:
            {
                const pType = dataTypeShaderLerpParametersType[dataType];
                builder.addUniform(`${dataType === DataType.INT32 ? 'i' : 'u'}vec2`, bName);
                builder.addUniform(`float`, sName);
                code += `
#define ${pName} ${pType}(${bName}[0], int(${bName}[1]), ${sName})
`;
                break;
            }
        case DataType.UINT64:
            {
                builder.addUniform(`uvec3`, bName);
                builder.addUniform(`float`, sName);
                code += `
#define ${pName} Uint64LerpParameters(uint64_t(${bName}.xy), int(${bName}[2]), ${sName})
`;
                break;
            }
    }
    return [glsl_dataTypeLerpParameters[dataType], code];
}
export function defineInvlerpShaderFunction(builder, name, dataType, clamp = false) {
    return [dataTypeShaderDefinition[dataType], defineLerpUniforms(builder, name, dataType), glsl_dataTypeComputeInvlerp[dataType], `
float ${name}(${getShaderType(dataType)} inputValue) {
  float v = computeInvlerp(inputValue, uLerpParams_${name});
  ${!clamp ? '' : 'v = clamp(v, 0.0, 1.0);'}
  return v;
}
`];
}
export function defineLerpShaderFunction(builder, name, dataType) {
    return [dataTypeShaderDefinition[dataType], defineLerpUniforms(builder, name, dataType), glsl_dataTypeComputeLerp[dataType], `
${getShaderType(dataType)} ${name}(float inputValue) {
  return computeLerp(inputValue, uLerpParams_${name});
}
`];
}
const tempUint64 = new Uint64();
export function enableLerpShaderFunction(shader, name, dataType, interval) {
    const gl = shader.gl;

    switch (dataType) {
        case DataType.INT8:
        case DataType.UINT8:
        case DataType.INT16:
        case DataType.UINT16:
        case DataType.FLOAT32:
            gl.uniform2f(shader.uniform(`uLerpParams_${name}`), interval[0], 1 / (interval[1] - interval[0]));
            break;
        case DataType.INT32:
        case DataType.UINT32:
            {
                const lower = interval[0];
                const diff = interval[1] - lower;
                const shift = Math.max(0, Math.ceil(_Math$log(Math.abs(diff))) - 24);
                const scalar = Math.pow(2, shift) / diff;
                const bLocation = shader.uniform(`uLerpBounds_${name}`);
                if (dataType === DataType.UINT32) {
                    gl.uniform2ui(bLocation, lower, shift);
                } else {
                    gl.uniform2i(bLocation, lower, shift);
                }
                gl.uniform1f(shader.uniform(`uLerpScalar_${name}`), scalar);
                break;
            }
        case DataType.UINT64:
            {
                const lower = interval[0];
                const upper = interval[1];
                Uint64.absDifference(tempUint64, upper, lower);
                const numBits = tempUint64.high > 0 ? 32 + Math.ceil(_Math$log(tempUint64.high)) : Math.ceil(_Math$log(tempUint64.low));
                const shift = Math.max(0, numBits - 24);
                Uint64.rshift(tempUint64, tempUint64, shift);
                let scalar = 1 / tempUint64.low;
                if (Uint64.compare(lower, upper) > 0) {
                    scalar *= -1;
                }
                const bLocation = shader.uniform(`uLerpBounds_${name}`);
                gl.uniform3ui(bLocation, lower.low, lower.high, shift);
                gl.uniform1f(shader.uniform(`uLerpScalar_${name}`), scalar);
            }
    }
}
//# sourceMappingURL=lerp.js.map